/**
 * Builds the message a user sees from an API error body.
 *
 * The API answers a validation failure with a summary plus the reasons:
 *
 *   { error: 'Role assignment validation failed', details: ['Cannot assign roles to ...'] }
 *
 * The summary names the category; the details are the actionable part, so both reach the screen.
 * Throwing `result.error` alone leaves the user with no indication of what to change.
 */

interface ApiErrorBody {
  error?: string;
  message?: string;
  details?: unknown;
}

/** Non-empty strings from a details array, trimmed. Anything else is ignored rather than stringified. */
function reasons(details: unknown): string[] {
  if (!Array.isArray(details)) return [];
  return details
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function apiErrorMessage(body: ApiErrorBody | null | undefined, fallback: string): string {
  const summary = body?.error?.trim() || body?.message?.trim() || '';
  const detail = reasons(body?.details);

  if (detail.length === 1) {
    // One reason reads better on its own than under a heading that only names the category.
    return detail[0];
  }

  if (detail.length > 1) {
    const lead = summary || fallback;
    return `${lead}\n${detail.map((entry) => `• ${entry}`).join('\n')}`;
  }

  return summary || fallback;
}

export default { apiErrorMessage };
